import React from 'react';
import type { YearBucket } from './yearAxis';
import './YearAxisControl.css';

export interface YearAxisControlProps {
  buckets: YearBucket[];
  /** The active ``?year=`` id — a year, the ``OHNE_JAHR`` sentinel, or null for all years. */
  activeYear: string | null;
  /** Select a year (or the sentinel), or ``null`` to clear back to all years. */
  onYearChange: (year: string | null) => void;
}

/**
 * Compact Erntejahr axis above the /farm feed (#1934) — the ff-chip visual
 * family, NOT a LensePanel pseudo-child (lens badges stay year-less). Renders
 * an "Alle" reset chip plus one chip per year bucket, each showing the
 * absolute per-year facet count. The control is stateless: selection lives in
 * the URL (``?year=``) and the parent folds it via ``withYearParam``. FarmPage
 * gates rendering (lens declares a ``year_attribute`` AND ≥2 buckets AND live
 * data) so this component always has a meaningful axis to draw.
 */
export const YearAxisControl: React.FC<YearAxisControlProps> = ({ buckets, activeYear, onYearChange }) => {
  return (
    <div className="farm-year-axis" role="group" aria-label="Erntejahr" data-testid="year-axis">
      <span className="farm-year-axis__label">Erntejahr</span>
      <button
        type="button"
        data-testid="year-chip-all"
        className={`farm-year-axis__chip${activeYear === null ? ' farm-year-axis__chip--active' : ''}`}
        aria-pressed={activeYear === null}
        onClick={() => onYearChange(null)}
      >
        Alle
      </button>
      {buckets.map((bucket) => {
        const active = activeYear === bucket.id;
        return (
          <button
            key={bucket.id}
            type="button"
            data-testid={`year-chip-${bucket.id}`}
            className={`farm-year-axis__chip${active ? ' farm-year-axis__chip--active' : ''}`}
            aria-pressed={active}
            onClick={() => onYearChange(bucket.id)}
          >
            <span className="farm-year-axis__chip-label">{bucket.label}</span>
            <span className="farm-year-axis__chip-count">{bucket.count}</span>
          </button>
        );
      })}
    </div>
  );
};
